import { dispatchOther } from '../redux/redux.utils';
import { setIsSignedIn } from '../redux/slices/graphlogin-slice';
import type { AccessToken, AuthResponse, RefreshToken } from '../types/auth';
import { isSafari } from '../utils/web';
import { sendToken } from './communication/authentication';
import {
  generatePopupAuthResultKey,
  generateSharedStorageKey,
  generateWebsiteStorageKey,
  generateWebsiteStorageRefreshKey,
  localStorage,
} from './storage/localStorage';

export const clearLocalToken = (key: string) => {
  localStorage.removeItem(generateWebsiteStorageKey(key));
  localStorage.removeItem(generateWebsiteStorageRefreshKey(key));
};
export const storeLocalToken = (key: string, token: AccessToken, refreshToken: RefreshToken) => {
  localStorage.setItem(generateWebsiteStorageKey(key), token || '');
  if (refreshToken) {
    localStorage.setItem(generateWebsiteStorageRefreshKey(key), refreshToken);
  } else if (refreshToken !== undefined) {
    localStorage.removeItem(generateWebsiteStorageRefreshKey(key));
  }
};
export const getLocalToken = (key: string) => localStorage.getItem(generateWebsiteStorageKey(key));

export function getLocalRefreshToken(key: string) {
  return localStorage.getItem(generateWebsiteStorageRefreshKey(key));
}
export function setLocalRefreshToken(key: string) {
  return localStorage.getItem(generateWebsiteStorageRefreshKey(key));
}

export const setGlobalToken = (key: string, token: AccessToken) => {
  // Because Safari partitions storage for 3rd parties
  if (isSafari()) {
    // There is no need to share value between contexts
    const oldValue = localStorage.getItem(generateWebsiteStorageKey(key));
    // Only update this context's storage if token is new and valid
    if (token && token !== oldValue) {
      localStorage.setItem(generateWebsiteStorageKey(key), token);
    }
  } else {
    if (token) {
      localStorage.setItem(generateSharedStorageKey(key), token);
      dispatchOther(setIsSignedIn(true));
    }
  }
};
export const getGlobalToken = (key: string) =>
  localStorage.getItem(isSafari() ? generateWebsiteStorageKey(key) : generateSharedStorageKey(key));

export function clearGlobalToken(key: string) {
  localStorage.removeItem(generateSharedStorageKey(key));
  dispatchOther(setIsSignedIn(false));
}

export function saveTokenInGL(key: string, authResponse: AuthResponse) {
  const { token, refreshToken } = authResponse;
  setGlobalToken(key, token);
  storeLocalToken(key, token, refreshToken);
}

export function saveAndBroadcastTokenInGL(
  key: string,
  authResponse: AuthResponse,
  targetOrigin: string,
) {
  saveTokenInGL(key, authResponse);
  sendToken(authResponse, targetOrigin);
}

export const saveInterfaceLanguage = (language: string) => {
  localStorage.setItem('lang', language);
};

export const getSavedInterfaceLanguage = () => localStorage.getItem('lang');

// Popup auth result - used as fallback when window.opener is null (iOS Safari with Facebook)
export interface PopupAuthResult {
  token: string;
  refreshToken?: string;
  key: string;
  network?: string;
  timestamp: number;
}

export function savePopupAuthResult(key: string, authResponse: AuthResponse, network?: string) {
  const result: PopupAuthResult = {
    token: authResponse.token || '',
    refreshToken: authResponse.refreshToken || undefined,
    key,
    network,
    timestamp: Date.now(),
  };
  localStorage.setItem(generatePopupAuthResultKey(key), JSON.stringify(result));
}

export function getPopupAuthResult(key: string): PopupAuthResult | null {
  const stored = localStorage.getItem(generatePopupAuthResultKey(key));
  if (!stored) return null;
  try {
    const result = JSON.parse(stored) as PopupAuthResult;
    // Expire after 30 seconds to avoid stale data
    if (Date.now() - result.timestamp > 30000) {
      clearPopupAuthResult(key);
      return null;
    }
    return result;
  } catch {
    return null;
  }
}

export function clearPopupAuthResult(key: string) {
  localStorage.removeItem(generatePopupAuthResultKey(key));
}
